import CameraAltIcon from "@mui/icons-material/CameraAlt";
import DocumentScannerIcon from "@mui/icons-material/DocumentScanner";
import { Box, Button, IconButton } from "@mui/material";
import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Webcam from "react-webcam";

const isMobileDevice = () => {
  return /Mobi|Android/i.test(navigator.userAgent);
};

const WebcamCapture = () => {
  const navigate = useNavigate();
  const webcamRef = useRef(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [showButton, setShowButton] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [facingMode, setFacingMode] = useState("user");
  const [cameraError, setCameraError] = useState(false); // Track camera access issues

  useEffect(() => {
    if (isMobileDevice()) {
      setFacingMode({ exact: "environment" }); // Use rear camera for mobile
    } else {
      setFacingMode("user"); // Use front camera for desktop/laptop
    }
  }, []);

  const capture = () => {
    if (webcamRef.current) {
      const imageSrc = webcamRef.current.getScreenshot();
      if (imageSrc) {
        setCapturedImage(imageSrc);
      } else {
        alert("Unable to capture image. Please check your camera.");
      }
    } else {
      alert("Webcam not available. Please enable camera access.");
    }
  };

  const sendToAPI = async () => {
    if (capturedImage) {
      setIsLoading(true);
      const base64Image = capturedImage.split(",")[1];

      const formData = new FormData();
      formData.append("image", base64Image);

      try {
        const response = await axios.post("/api/transcribe/image", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        console.log("Image uploaded successfully:", response.data);
        const parsedResponse = JSON.parse(
          response.data.response.replace(/'/g, '"')
        );
        navigate(
          `/products/details/${
            parsedResponse?.object +
            " " +
            parsedResponse?.brand +
            " " +
            parsedResponse?.color
          }`
        );
      } catch (error) {
        console.error("Error uploading image:", error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const deleteCapture = () => {
    setCapturedImage(null);
  };

  return (
    <Box
      sx={{
        overflowY: "auto",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        alignItems: "center",
        minHeight: "100vh",
      }}
    >
      {/* Webcam section */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          height: "50vh",
          marginBottom: "20px",
          overflow: "hidden",
        }}
      >
        {!cameraError ? (
          <Webcam
            audio={false}
            ref={webcamRef}
            screenshotFormat="image/jpeg"
            onUserMediaError={() => {
              setCameraError(true);
              alert("Camera access denied. Please enable it in browser settings.");
            }}
            videoConstraints={{
              facingMode: facingMode,
            }}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              borderRadius: "0 0 20px 20px",
            }}
          />
        ) : (
          <p style={{ color: "red", fontSize: "1.2rem", textAlign: "center" }}>
            Camera access is blocked. Please allow camera permissions in your browser settings.
          </p>
        )}
      </Box>

      {/* Bottom section with buttons and icon */}
      <Box
        sx={{
          backgroundColor: "white",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          bottom: 0,
          position: "fixed",
          width: "100%",
          height: "40vh",
          borderRadius: "40px 40px 0 0",
        }}
      >
        {showButton && !cameraError && (
          <Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                marginTop: "-30vh",
              }}
            >
              <IconButton
                sx={{
                  color: "white",
                  backgroundColor: "#EF4F26",
                  transform: "scale(1.7)",
                  mb: 2,
                }}
              >
                <DocumentScannerIcon />
              </IconButton>
            </Box>

            {/* Capture photo button */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                padding: 2,
                marginBottom: 1,
              }}
            >
              <Box
                onClick={() => {
                  capture();
                  setShowButton(false);
                }}
                sx={{
                  backgroundColor: "#F6F6F6",
                  color: "#205375",
                  padding: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  borderRadius: 5,
                }}
              >
                <IconButton>
                  <CameraAltIcon sx={{ color: "#205375" }} />
                </IconButton>
                <span>Capture photo</span>
              </Box>
            </Box>
          </Box>
        )}

        {/* Captured image display */}
        {capturedImage && (
          <Box sx={{ mt: "-20vh" }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                mb: 2,
              }}
            >
              <img
                src={capturedImage}
                alt="Captured"
                style={{
                  width: "25vh",
                  height: "auto",
                  borderRadius: "10px",
                }}
              />
            </Box>

            {/* Upload and Delete buttons */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                gap: 2,
                marginBottom: 2,
                marginTop: 2,
              }}
            >
              <Button
                onClick={sendToAPI}
                disabled={isLoading}
                sx={{ backgroundColor: "#F6F6F6", padding: 1.5 }}
              >
                {isLoading ? "Uploading..." : "Upload to API"}
              </Button>
              <Button
                onClick={() => {
                  deleteCapture();
                  setShowButton(true);
                }}
                sx={{ backgroundColor: "#F6F6F6", padding: 1.5 }}
              >
                Delete photo
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default WebcamCapture;
